
import { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";
type MetalTheme = "gold" | "silver" | null;

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultMetal?: MetalTheme;
  storageKey?: string;
  metalStorageKey?: string;
  ssrMode?: boolean;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  metalTheme: MetalTheme;
  setMetalTheme: (metal: MetalTheme) => void;
  toggleMetalTheme: () => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  metalTheme: null,
  setMetalTheme: () => null,
  toggleMetalTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "system",
  defaultMetal = "gold",
  storageKey = "gildy-ui-theme",
  metalStorageKey = "gildy-ui-metal",
  ssrMode = false,
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const [metalTheme, setMetalTheme] = useState<MetalTheme>(defaultMetal);
  const [mounted, setMounted] = useState<boolean>(false);

  // Only on client side, after mounting, load preferences from localStorage
  useEffect(() => {
    // Exit early during SSR
    if (typeof window === 'undefined') return;
    
    // Set mounted to true when we're on the client
    setMounted(true);
    
    // Only attempt to load from localStorage when we're certain we're client-side
    try {
      // Load theme from localStorage if available
      const savedTheme = localStorage.getItem(storageKey) as Theme | null;
      if (savedTheme) {
        setTheme(savedTheme);
      }
      
      // Load metal theme from localStorage if available
      const savedMetal = localStorage.getItem(metalStorageKey) as MetalTheme | null;
      if (savedMetal) {
        setMetalTheme(savedMetal);
      }
    } catch (e) {
      // Ignore localStorage errors (e.g., in incognito mode)
      console.warn("Could not access localStorage:", e);
    }
  }, [storageKey, metalStorageKey, defaultTheme, defaultMetal, ssrMode]);

  // Update theme class on document when theme changes
  useEffect(() => {
    // Exit early during SSR
    if (typeof window === 'undefined') return;
    
    // Wait until component is mounted
    if (!mounted) return;
    
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
    
    // Add transition class for smooth theme changes
    root.style.transition = "background-color 0.5s ease, color 0.5s ease";
  }, [theme, mounted]);

  // Update metal theme class when it changes
  useEffect(() => {
    // Exit early during SSR
    if (typeof window === 'undefined') return;
    
    // Wait until component is mounted
    if (!mounted) return;
    
    const root = window.document.documentElement;
    root.classList.remove("gold-theme", "silver-theme");
    
    if (metalTheme) {
      root.classList.add(`${metalTheme}-theme`);
    }
  }, [metalTheme, mounted]);

  const toggleMetalTheme = () => {
    // Exit early during SSR
    if (typeof window === 'undefined') return;
    
    // Wait until component is mounted
    if (!mounted) return;
    
    try {
      const newMetal = metalTheme === "gold" ? "silver" : "gold";
      localStorage.setItem(metalStorageKey, newMetal);
      setMetalTheme(newMetal);
    } catch (e) {
      // Handle localStorage errors
      console.warn("Could not update localStorage:", e);
      setMetalTheme(metalTheme === "gold" ? "silver" : "gold");
    }
  };

  // For SSR or before client mount, use default values
  if (!mounted) {
    return (
      <ThemeProviderContext.Provider
        {...props}
        value={{
          theme: defaultTheme,
          setTheme: () => null,
          metalTheme: defaultMetal,
          setMetalTheme: () => null,
          toggleMetalTheme: () => null
        }}
      >
        {children}
      </ThemeProviderContext.Provider>
    );
  }

  // For fully mounted client, use full version with state
  const value = {
    theme,
    setTheme: (theme: Theme) => {
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(storageKey, theme);
        }
      } catch (e) {
        console.warn("Could not store theme preference:", e);
      }
      setTheme(theme);
    },
    metalTheme,
    setMetalTheme: (metal: MetalTheme) => {
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(metalStorageKey, metal || "");
        }
      } catch (e) {
        console.warn("Could not store metal theme preference:", e);
      }
      setMetalTheme(metal);
    },
    toggleMetalTheme
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
